import {
  getVehicleLabel,
  type Vehicle,
  type VehicleDisablerStatus,
  type VehicleDriver,
  type VehicleDriverAuthorizationMode,
  type VehicleLastLocation,
  type VehicleType,
} from '@cobliteam/common-types';
import type { DisablerDto, VehicleDriverDto, VehicleDto, VehicleLastLocationDto, VehicleModelDto } from '../../__generated__/graphql';
import type { DeviceVehicle } from '../device/definitions';
import { fuelTypeAPItoIFuelType, vehicleSizeAPIToISize } from './APITransformer';
import type { APIFuel, APISize } from './Types';

const convertVehicleModel = (modelDto: VehicleModelDto): Vehicle['model'] => ({
  name: modelDto.name ?? undefined,
  brand: modelDto.brand ?? undefined,
  type: modelDto.type as VehicleType | undefined,
  year: modelDto.year ?? undefined,
});

const convertVehicleFuel = (fuelDto: VehicleDto['fuel']): Vehicle['fuel'] => {
  if (!fuelDto) return undefined;
  return {
    type: fuelDto.type ? fuelTypeAPItoIFuelType(fuelDto.type as APIFuel) : undefined,
    consumption: fuelDto.consumption ?? undefined,
    price: fuelDto.price ?? undefined,
  };
};

const convertLastLocation = (lastLocationDto: VehicleLastLocationDto | null | undefined): VehicleLastLocation | undefined => {
  if (!lastLocationDto) return undefined;
  return {
    timeInMillis: lastLocationDto.timeInMillis ?? undefined,
    cobliIngestedAt: lastLocationDto.cobliIngestedAt ?? undefined,
    isPlugged: lastLocationDto.isPlugged ?? undefined,
  };
};

const convertDisablerStatus = (disablerDto: DisablerDto | null | undefined): VehicleDisablerStatus | undefined => {
  if (!disablerDto) return undefined;
  return {
    lastDisablingAction: disablerDto.lastDisablingAction ?? undefined,
    lastDisablingActionTime: disablerDto.lastDisablingActionTime ?? undefined,
    status: disablerDto.status ?? undefined,
  };
};

const convertDriver = (driverDto: VehicleDriverDto | null | undefined): VehicleDriver | undefined => {
  if (!driverDto) return undefined;
  return {
    id: driverDto.id ?? '',
    name: driverDto.name ?? '',
    phoneNumbers: driverDto.phoneNumbers?.filter((phone): phone is string => phone !== null) ?? [],
  };
};

export const graphQLToVehicle = (vehicle: VehicleDto): Vehicle => {
  return {
    ...vehicle,
    // Core identifiers
    id: vehicle.id,
    fleetId: vehicle.fleetId,

    // Basic info
    licensePlate: vehicle.licensePlate ?? undefined,
    alias: vehicle.alias ?? undefined,
    label: getVehicleLabel(vehicle.licensePlate ?? undefined, vehicle.model.name ?? undefined, vehicle.model.brand ?? undefined, vehicle.device?.cobliId),
    model: convertVehicleModel(vehicle.model),
    size: vehicle.size ? vehicleSizeAPIToISize(vehicle.size as APISize) : undefined,

    // Device related
    deviceId: vehicle.device?.id,
    deviceType: vehicle.device?.type,
    cobliId: vehicle.device?.cobliId,

    // Groups and tags
    groups: vehicle.groups,
    tagId: vehicle.tagId ?? undefined,
    tags: vehicle.tags ?? undefined,
    groupPath: vehicle.groupPath ?? undefined,

    // Fuel and performance
    fuel: convertVehicleFuel(vehicle.fuel),
    fuelConsumption: vehicle.fuelConsumption ?? undefined,
    speedLimitInKmh: vehicle.speedLimitInKmh ?? undefined,

    // Status and tracking
    lastLocation: convertLastLocation(vehicle.lastLocation),
    odometer: vehicle.odometer ?? undefined,
    disablerStatus: convertDisablerStatus(vehicle.disablerStatus),

    // Driver and authorizations
    driver: convertDriver(vehicle.driver),
    driverId: vehicle.driver?.id ?? undefined,

    // Security settings
    isAllowListEnabled: vehicle.isAllowListEnabled ?? undefined,
    isImmobilizerEnabled: vehicle.isImmobilizerEnabled ?? undefined,
    vehicleDriverAuthorizationMode: (vehicle.vehicleDriverAuthorizationMode as VehicleDriverAuthorizationMode) ?? undefined,
    immobilizerStatus: vehicle.immobilizerStatus ?? undefined,
  };
};

export const graphQLToDeviceVehicle = (vehicle: VehicleDto): DeviceVehicle => ({
  cobliId: vehicle.device?.cobliId,
  deviceId: vehicle.device?.id,
  id: vehicle.id,
  licensePlate: vehicle.licensePlate ?? undefined,
  model: vehicle.model.name ?? undefined,
  brand: vehicle.model.brand ?? undefined,
});
